import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, CircularProgress, Typography, ToggleButton } from '@mui/material';
import { DataGrid, gridClasses, GridToolbar } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { lightGreen, red } from '@mui/material/colors';
import { useLayout } from '../../context/LayoutContext';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'react-toastify';

import { useGetShiftRequestsByShiftIdQuery } from '../../slices/shiftRequestApiSlice';
import { useGetAgenciesQuery } from '../../slices/agencyApiSlice';
import {
  useAssignShiftToAgencyMutation,
  useGetShiftsQuery,
  useAssignShiftToUserMutation,
  useGetApprovedFilledShiftsQuery,
} from '../../slices/shiftApiSlice';
import { useUpdateShiftRequestStatusMutation } from '../../slices/shiftRequestApiSlice';

// ─── Persistence keys ─────────────────────────────────────────────────────────
const LS_SORT  = 'shiftWardList_sortModel';
const LS_FILTER = 'shiftWardList_filterModel';
const LS_PAST  = 'shiftWardList_showPastOnly';

const loadLS = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v !== null ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
/**
 * Format a date value to local time string.
 * Avoids the UTC display bug in MUI DataGrid's built-in dateTime formatter.
 */
const fmtLocal = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  return d.toLocaleString(undefined, {
    day:    '2-digit',
    month:  '2-digit',
    year:   'numeric',
    hour:   '2-digit',
    minute: '2-digit',
    hour12: false,   // ← set true if you prefer 12-hr
  });
};

// ─── Styled grid ──────────────────────────────────────────────────────────────
const StyledDataGrid = styled(DataGrid)`
  & .${gridClasses.row}.lightGreenRow  { background-color: ${lightGreen[100]}; }
  & .${gridClasses.row}.redRow         { background-color: ${red[100]}; }
  & .${gridClasses.row}.lightOrangeRow { background-color: #ffe0b2; }
  & .${gridClasses.row}.greenRow       { background-color: #c8e6c9; }
  & .${gridClasses.row}.darkGreenRow   { background-color: rgb(149, 240, 152); }
`;

// ─── Component ────────────────────────────────────────────────────────────────
const ShiftWardListScreen = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth);
  const { setFluid } = useLayout();

  // ── Preserved state (survives navigate-away + back) ──────────────────────
  const [sortModel, setSortModel] = useState(
    () => loadLS(LS_SORT, [{ field: 'startDateTime', sort: 'asc' }])
  );
  const [filterModel, setFilterModel] = useState(
    () => loadLS(LS_FILTER, { items: [] })
  );
  const [showPastOnly, setShowPastOnly] = useState(
    () => loadLS(LS_PAST, false)
  );

  const handleSortChange = useCallback((model) => {
    setSortModel(model);
    localStorage.setItem(LS_SORT, JSON.stringify(model));
  }, []);

  const handleFilterChange = useCallback((model) => {
    setFilterModel(model);
    localStorage.setItem(LS_FILTER, JSON.stringify(model));
  }, []);

  const togglePastOnly = () => {
    setShowPastOnly((prev) => {
      const next = !prev;
      localStorage.setItem(LS_PAST, JSON.stringify(next));
      return next;
    });
  };

  // ── Data ─────────────────────────────────────────────────────────────────
  const {
    data: shifts,
    isLoading,
    isError,
    error,
    refetch: refetchShifts,
  } = useGetApprovedFilledShiftsQuery();

  // Client-side past-shifts filter applied on top of whatever the grid filters
  const visibleShifts = React.useMemo(() => {
    if (!shifts) return [];
    if (!showPastOnly) return shifts;
    const now = new Date();
    return shifts.filter((s) => new Date(s.endDateTime) < now);
  }, [shifts, showPastOnly]);

  // ── Modal / assignment state ──────────────────────────────────────────────
  const [openModal, setOpenModal]           = useState(false);
  const [selectedShift, setSelectedShift]   = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [selectedFiller, setSelectedFiller] = useState(null);
  const [isAgencyFilling, setIsAgencyFilling] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);

  const { data: shiftRequests, refetch } = useGetShiftRequestsByShiftIdQuery(
    selectedShiftId, { skip: !selectedShiftId }
  );
  const { data: agencies } = useGetAgenciesQuery();
  const [assignShift]         = useAssignShiftToUserMutation();
  const [assignShiftToAgency] = useAssignShiftToAgencyMutation();
  const [updateShiftRequestStatus] = useUpdateShiftRequestStatusMutation();

  useEffect(() => {
    return () => setFluid(false);
  }, [setFluid]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAssignClick = (shift, filler, isAgency = false) => {
    setSelectedShift(shift);
    setSelectedFiller(filler);
    setSelectedShiftId(shift._id);
    setIsAgencyFilling(isAgency);
    setOpenConfirmModal(true);
  };

  const handleAgencyApprove = async (agency) => {
    try {
      await assignShiftToAgency({
        shiftId:    selectedShiftId,
        agencyId:   null,
        agencyName: '',
        agencyEmail:'',
        managerId:  userInfo._id,
        lineStatus: 'Open',
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Failed to assign shift to agency:', err);
    }
  };

  const handleApprove = async (request) => {
    try {
      await assignShift({
        shiftId:   selectedShiftId,
        userId:    null,
        userName:  '',
        userEmail: '',
        managerId: userInfo._id,
        lineStatus:'Open',
      }).unwrap();
      refetch();
    } catch (err) {
      console.error('Failed to assign shift:', err);
    }
  };

  const handleConfirmAssignment = async () => {
    try {
      if (!selectedShift || !selectedFiller) {
        toast.error('Please select both a shift and a filler');
        return;
      }

      if (isAgencyFilling) {
        await handleAgencyApprove(selectedFiller);
      } else {
        await handleApprove({ requestedBy: null, lineStatus: 'Open' });
      }

      const approvedRequest = shiftRequests?.find(
        (req) => req.lineStatus === 'Approved'
      );
      if (approvedRequest) {
        await updateShiftRequestStatus({
          shiftRequestId: approvedRequest._id,
          lineStatus: 'Open',
        }).unwrap();
      }

      refetch();
      await refetchShifts();
      setOpenConfirmModal(false);
      setOpenModal(false);
      setSelectedShift(null);
      setSelectedFiller(null);
      toast.success('Shift Reset!');
    } catch (error) {
      console.error('Assignment failed:', error);
      toast.error('Failed to reset shift');
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      field: 'startDateTime',
      headerName: 'Start Time',
      width: 170,
      type: 'dateTime',                              // keeps sort working correctly
      valueGetter: (params) => new Date(params.row.startDateTime),
      renderCell: (params) => fmtLocal(params.value), // ← local time, not UTC
    },
    {
      field: 'endDateTime',
      headerName: 'End Time',
      width: 170,
      type: 'dateTime',
      valueGetter: (params) => new Date(params.row.endDateTime),
      renderCell: (params) => fmtLocal(params.value), // ← local time, not UTC
    },
    { field: 'wardDepartmentArea',  headerName: 'Ward/Dept Area',       width: 150 },
    { field: 'categoryOfStaff',     headerName: 'Staff Category',        width: 150 },
    { field: 'lineStatus',          headerName: 'Line Status',           width: 120 },
    { field: 'filledByName',        headerName: 'Filled By',             width: 120 },
    { field: 'specialRequirements', headerName: 'Special Requirements',  width: 150 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable:   false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={(e) => {
            e.stopPropagation(); // prevent row-click opening the modal
            navigate(`/admin/shiftsWard/${params.row._id}/edit`);
          }}
        >
          Approve
        </Button>
      ),
    },
  ];

  // ── Row colours ───────────────────────────────────────────────────────────
  const getRowClassName = (params) => {
    const now    = new Date();
    const end    = new Date(params.row.endDateTime);
    if (end < now) return 'redRow';

    switch (params.row.lineStatus?.toLowerCase()) {
      case 'open':
      case 'on hold':       return 'lightOrangeRow';
      case 'archived':
      case 'filledbystaff':
      case 'filled':
      case 'complete':      return 'greenRow';
      case 'filledbyagency':return 'darkGreenRow';
      case 'cancelled':     return 'redRow';
      default:              return 'lightGreenRow';
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <CircularProgress />
    </Box>
  );

  if (isError) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <Typography color="error">Error: {error.message}</Typography>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', p: 2 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Ward — Approve and update worked shifts</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>

          {/* Past-shifts toggle */}
          <ToggleButton
            value="pastOnly"
            selected={showPastOnly}
            onChange={togglePastOnly}
            size="small"
            color="warning"
            sx={{ textTransform: 'none' }}
          >
            <HistoryIcon sx={{ mr: 0.5 }} fontSize="small" />
            {showPastOnly ? 'Past shifts only' : 'All shifts'}
          </ToggleButton>

          <Button variant="contained" color="primary">
            Create Shift
          </Button>
        </Box>
      </Box>

      {/* ── Grid ── */}
      <StyledDataGrid
        density="compact"
        rows={visibleShifts}
        columns={columns}
        getRowId={(row) => row._id}
        getRowClassName={getRowClassName}

        // ── Controlled sort — persisted to localStorage ──
        sortModel={sortModel}
        onSortModelChange={handleSortChange}

        // ── Controlled filter — persisted to localStorage ──
        filterModel={filterModel}
        onFilterModelChange={handleFilterChange}

        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        slots={{ toolbar: GridToolbar }}
        slotProps={{ toolbar: { showQuickFilter: true } }}
        disableSelectionOnClick
        onRowClick={(params) => {
          setSelectedShift(params.row);
          setSelectedShiftId(params.row._id);
          setOpenModal(true);
        }}
      />

      {/* ── Detail modal ── */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Shift Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {selectedShift ? (
            <Box>
              <Typography variant="body1"><strong>Start:</strong> {fmtLocal(selectedShift.startDateTime)}</Typography>
              <Typography variant="body1"><strong>End:</strong>   {fmtLocal(selectedShift.endDateTime)}</Typography>
              <Typography variant="body1"><strong>Ward/Dept:</strong>         {selectedShift.wardDepartmentArea}</Typography>
              <Typography variant="body1"><strong>Category of Staff:</strong> {selectedShift.categoryOfStaff}</Typography>
              <Typography variant="body1"><strong>Line Status:</strong>       {selectedShift.lineStatus}</Typography>
              <Typography variant="body1"><strong>Filled By:</strong>         {selectedShift.filledByName || 'Unassigned'}</Typography>
              <Typography variant="body1"><strong>Special Requirements:</strong> {selectedShift.specialRequirements || 'None'}</Typography>
            </Box>
          ) : (
            <Typography>No shift selected.</Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleConfirmAssignment} color="primary" variant="contained">
            Confirm Shift Reset
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ShiftWardListScreen;